import React from 'react';
import { XIcon, ChevronDownIcon } from 'lucide-react';
interface AdvancedSearchModalProps {
  isOpen: boolean;
  onClose: () => void;
  searchQuery: string;
  onSearchQueryChange: (value: string) => void;
  languageFilter: string;
  onLanguageFilterChange: (value: string) => void;
  brandFilter: string;
  onBrandFilterChange: (value: string) => void;
  dateFilter: string;
  onDateFilterChange: (value: string) => void;
  categoryFilter: string;
  onCategoryFilterChange: (value: string) => void;
  regionFilter: string;
  onRegionFilterChange: (value: string) => void;
  onApply: () => void;
  onClearAll: () => void;
}
export function AdvancedSearchModal({
  isOpen,
  onClose,
  searchQuery,
  onSearchQueryChange,
  languageFilter,
  onLanguageFilterChange,
  brandFilter,
  onBrandFilterChange,
  dateFilter,
  onDateFilterChange,
  categoryFilter,
  onCategoryFilterChange,
  regionFilter,
  onRegionFilterChange,
  onApply,
  onClearAll
}: AdvancedSearchModalProps) {
  if (!isOpen) return null;
  return <div className="fixed inset-0 bg-black bg-opacity-50 flex items-start justify-center z-50 overflow-y-auto pt-20 pb-20" data-id="element-16">
      <div className="bg-white rounded-lg shadow-2xl w-full max-w-4xl mx-4" data-id="element-17">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-neutral-200" style={{
        background: 'linear-gradient(135deg, #1e3a52 0%, #182941 50%, #12202f 100%)'
      }} data-id="element-18">
          <h2 className="text-2xl font-bold text-white" data-id="element-19">Advanced Search</h2>
          <button onClick={onClose} className="text-white hover:text-neutral-200 transition-colors" data-id="element-20">
            <XIcon className="w-6 h-6" data-id="element-21" />
          </button>
        </div>
        {/* Content */}
        <div className="p-6 space-y-6" data-id="element-22">
          {/* Articles containing words */}
          <div data-id="element-23">
            <label className="block text-sm font-semibold text-neutral-900 mb-3" data-id="element-24">
              Articles containing words
            </label>
            <input type="text" value={searchQuery} onChange={e => onSearchQueryChange(e.target.value)} placeholder="Enter words to search for in articles..." className="w-full px-4 py-3 border border-neutral-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent transition-all" data-id="element-25" />
            <p className="text-xs text-neutral-500 mt-2" data-id="element-26">
              Enter words separated by spaces to search within article content
            </p>
          </div>
          {/* Filter by Topics */}
          <div data-id="element-27">
            <label className="block text-sm font-semibold text-neutral-900 mb-3" data-id="element-28">
              Filter by Topics
            </label>
            <select value={categoryFilter} onChange={e => onCategoryFilterChange(e.target.value)} className="w-full px-4 py-3 border border-neutral-300 rounded-lg bg-white focus:outline-none focus:ring-2 transition-all" style={{
            '--tw-ring-color': '#0289BB'
          } as React.CSSProperties} data-id="element-29">
              <option value="all" data-id="element-30">All Categories</option>
              <option value="General News" data-id="element-31">General News</option>
              <option value="Politics" data-id="element-32">Politics</option>
              <option value="Business" data-id="element-33">Business</option>
              <option value="Sports" data-id="element-34">Sports</option>
              <option value="Technology" data-id="element-35">Technology</option>
              <option value="Environment" data-id="element-36">Environment</option>
              <option value="Science" data-id="element-37">Science</option>
            </select>
          </div>
          {/* Places Covered */}
          <div data-id="element-38">
            <label className="block text-sm font-semibold text-neutral-900 mb-3" data-id="element-39">
              Places Covered
            </label>
            <select value={regionFilter} onChange={e => onRegionFilterChange(e.target.value)} className="w-full px-4 py-3 border border-neutral-300 rounded-lg bg-white focus:outline-none focus:ring-2 transition-all" style={{
            '--tw-ring-color': '#0289BB'
          } as React.CSSProperties} data-id="element-40">
              <option value="all" data-id="element-41">All Regions</option>
              <option value="United States" data-id="element-42">United States</option>
              <option value="United Kingdom" data-id="element-43">United Kingdom</option>
              <option value="Europe" data-id="element-44">Europe</option>
              <option value="Asia" data-id="element-45">Asia</option>
            </select>
          </div>
          {/* Language */}
          <div data-id="element-46">
            <label className="block text-sm font-semibold text-neutral-900 mb-3" data-id="element-47">
              Language
            </label>
            <select value={languageFilter} onChange={e => onLanguageFilterChange(e.target.value)} className="w-full px-4 py-3 border border-neutral-300 rounded-lg bg-white focus:outline-none focus:ring-2 transition-all" style={{
            '--tw-ring-color': '#0289BB'
          } as React.CSSProperties} data-id="element-48">
              <option value="all" data-id="element-49">All Languages</option>
              <option value="English" data-id="element-50">English</option>
              <option value="Spanish" data-id="element-51">Spanish</option>
              <option value="French" data-id="element-52">French</option>
            </select>
          </div>
          {/* Filter by Brands */}
          <div data-id="element-53">
            <label className="block text-sm font-semibold text-neutral-900 mb-3" data-id="element-54">
              Filter by Brands
            </label>
            <select value={brandFilter} onChange={e => onBrandFilterChange(e.target.value)} className="w-full px-4 py-3 border border-neutral-300 rounded-lg bg-white focus:outline-none focus:ring-2 transition-all" style={{
            '--tw-ring-color': '#0289BB'
          } as React.CSSProperties} data-id="element-55">
              <option value="all" data-id="element-56">All Sources</option>
              <option value="Global News Network" data-id="element-57">Global News Network</option>
              <option value="Tech Today" data-id="element-58">Tech Today</option>
              <option value="Sports Daily" data-id="element-59">Sports Daily</option>
              <option value="Financial Times" data-id="element-60">Financial Times</option>
              <option value="Science Weekly" data-id="element-61">Science Weekly</option>
            </select>
          </div>
          {/* Time Range */}
          <div data-id="element-62">
            <label className="block text-sm font-semibold text-neutral-900 mb-3" data-id="element-63">
              Time Range
            </label>
            <select value={dateFilter} onChange={e => onDateFilterChange(e.target.value)} className="w-full px-4 py-3 border border-neutral-300 rounded-lg bg-white focus:outline-none focus:ring-2 transition-all" style={{
            '--tw-ring-color': '#0289BB'
          } as React.CSSProperties} data-id="element-64">
              <option value="all" data-id="element-65">All Time</option>
              <option value="today" data-id="element-66">Today</option>
              <option value="week" data-id="element-67">This Week</option>
              <option value="month" data-id="element-68">This Month</option>
            </select>
          </div>
        </div>
        {/* Footer */}
        <div className="flex items-center justify-between p-6 border-t border-neutral-200 bg-neutral-50" data-id="element-69">
          <button onClick={onClearAll} className="text-neutral-600 hover:text-neutral-900 font-medium transition-colors" data-id="element-70">
            Clear All Filters
          </button>
          <div className="flex items-center gap-3" data-id="element-71">
            <button onClick={onClose} className="px-6 py-2.5 border border-neutral-300 rounded-lg hover:bg-neutral-100 transition-all font-medium text-neutral-700" data-id="element-72">
              Cancel
            </button>
            <button onClick={() => {
            onApply();
            onClose();
          }} className="px-6 py-2.5 text-white rounded-lg hover:shadow-md transition-all font-medium" style={{
            background: 'linear-gradient(135deg, #26aedb 0%, #0289BB 50%, #0277a8 100%)'
          }} data-id="element-73">
              Apply Filters
            </button>
          </div>
        </div>
      </div>
    </div>;
}